/**
 * Modal "Autorizar e classificar" (Transition Form): o gestor autoriza a
 * solicitação e, no mesmo passo, classifica cada item nos cinco eixos da
 * não-qualidade — demanda, não conformidade, processo de origem, causa-raiz
 * e imputação. Também serve para RECLASSIFICAR (modo "classificar") em
 * qualquer situação.
 *
 * O formulário do celular do montador não muda: o motivo que ele informou
 * aparece aqui só como pista (NCs "sugeridas pelo motivo" vêm primeiro).
 * A imputação é sugerida pela regra do backend enquanto a pessoa escolhe;
 * escolher à mão trava a sugestão.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { FiInfo } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import {
  autorizarSolicitacao,
  classificarSolicitacao,
  obterAssistencia,
  obterCatalogosSelect,
  obterConfiguracaoSolicitacoes,
  obterImputacaoSugerida,
  obterRequisicao,
} from "../../../services/solicitacoes";
import { Carregando } from "../_shared/Estados";
import {
  EIXOS,
  aplicarATodas,
  linhasIncompletas,
  linhasIniciais,
  montarClassificacoes,
  motivoDoItem,
  ncsSugeridasParaMotivo,
  precisaClassificar,
  processosSugeridosPelaNc,
} from "./classificacao";
import { TIPO_LABELS, formatMedidas } from "./helpers";
import {
  ErrorMsg,
  Field,
  GhostBtn,
  InlineError,
  Input,
  Label,
  NeutralBanner,
  PecaCard,
  PecaHead,
  PecaLinha,
  Pill,
  RowEnd,
  Select,
} from "./styles";

const Grade = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 10px;
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

const ModeloBox = styled.div`
  border: 1px dashed var(--border);
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: var(--surface-2);
`;

const Sugestao = styled.button`
  border: 0;
  background: transparent;
  color: var(--accent);
  font-size: 11.5px;
  padding: 0;
  cursor: pointer;
  text-align: left;
  &:hover { text-decoration: underline; }
`;

const Lista = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 14px;
`;

const Banner = styled(NeutralBanner)`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  svg { flex: none; margin-top: 2px; }
`;

const ROTULO_EIXO = {
  demanda: "Demanda (o que se pede)",
  nao_conformidade: "Não conformidade (sintoma)",
  processo_origem: "Processo de origem (onde nasceu)",
  causa_raiz: "Causa-raiz (por quê)",
};

function opcoesDe(lista, rotulo = (o) => o.nome) {
  return (lista || []).map((o) => (
    <option key={o.codigo} value={o.codigo}>{rotulo(o)}</option>
  ));
}

/** Select de um eixo com grupo "Sugeridas" no topo quando há sugestão. */
function SelectEixo({ eixo, valor, onChange, catalogo, sugeridas }) {
  const sugeridasCod = new Set((sugeridas || []).map((s) => s.codigo));
  const restantes = (catalogo || []).filter((o) => !sugeridasCod.has(o.codigo));
  return (
    <Field>
      <Label>{ROTULO_EIXO[eixo]}</Label>
      <Select value={valor} onChange={(e) => onChange(e.target.value)}>
        <option value="">— não informado —</option>
        {sugeridas?.length > 0 && (
          <optgroup label="Sugeridas">{opcoesDe(sugeridas)}</optgroup>
        )}
        {sugeridas?.length > 0 ? (
          <optgroup label="Todas">{opcoesDe(restantes)}</optgroup>
        ) : opcoesDe(restantes)}
      </Select>
    </Field>
  );
}

function CamposLinha({ linha, catalogos, motivo, onCampo, mostrarResponsavel = true }) {
  const ncsSugeridas = useMemo(
    () => ncsSugeridasParaMotivo(catalogos.ncs, motivo), [catalogos.ncs, motivo],
  );
  const processosSugeridos = useMemo(
    () => processosSugeridosPelaNc(catalogos.processos, catalogos.ncs, linha.nao_conformidade),
    [catalogos.processos, catalogos.ncs, linha.nao_conformidade],
  );
  const imputacaoLabel = (c) => catalogos.imputacoes.find((i) => i.codigo === c)?.nome || c;
  return (
    <Grade>
      <SelectEixo eixo="demanda" valor={linha.demanda} catalogo={catalogos.demandas}
                  onChange={(v) => onCampo("demanda", v)} />
      <SelectEixo eixo="nao_conformidade" valor={linha.nao_conformidade} catalogo={catalogos.ncs}
                  sugeridas={ncsSugeridas} onChange={(v) => onCampo("nao_conformidade", v)} />
      <SelectEixo eixo="processo_origem" valor={linha.processo_origem} catalogo={catalogos.processos}
                  sugeridas={processosSugeridos} onChange={(v) => onCampo("processo_origem", v)} />
      <SelectEixo eixo="causa_raiz" valor={linha.causa_raiz} catalogo={catalogos.causas}
                  onChange={(v) => onCampo("causa_raiz", v)} />
      <Field>
        <Label>Imputação (quem arca)</Label>
        <Select value={linha.imputacao} onChange={(e) => onCampo("imputacao", e.target.value, true)}>
          <option value="">— pela regra —</option>
          {(catalogos.imputacoes || []).map((i) => (
            <option key={i.codigo} value={i.codigo}>{i.nome}</option>
          ))}
        </Select>
        {linha.imputacao_sugerida && linha.imputacao_sugerida !== linha.imputacao && (
          <Sugestao type="button" onClick={() => onCampo("imputacao", linha.imputacao_sugerida, false)}>
            Regra sugere: {imputacaoLabel(linha.imputacao_sugerida)} — usar
          </Sugestao>
        )}
      </Field>
      {mostrarResponsavel && (
        <Field>
          <Label>Responsável pela NC (opcional)</Label>
          <Input value={linha.responsavel_nc_nome}
                 onChange={(e) => onCampo("responsavel_nc_nome", e.target.value)}
                 placeholder="Nome de quem originou" />
        </Field>
      )}
    </Grade>
  );
}

function tituloDoItem(familia, item, idx) {
  if (familia === "assistencia") return item.codigo_peca || item.descricao || `Peça ${idx + 1}`;
  return item.codigo || item.descricao || `Item ${idx + 1}`;
}

export default function AutorizarClassificarModal({
  familia, id, numero, modo = "autorizar", onClose, onDone,
}) {
  const { request } = useAuth();
  const [dado, setDado] = useState(null);
  const [catalogos, setCatalogos] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [erro, setErro] = useState("");
  const [erroEnvio, setErroEnvio] = useState("");
  const [linhas, setLinhas] = useState([]);
  const [modelo, setModelo] = useState({
    demanda: "", nao_conformidade: "", processo_origem: "", causa_raiz: "",
    imputacao: "", responsavel_nc_nome: "", imputacao_sugerida: "",
  });
  const sugestaoSeq = useRef({});

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const [detalhe, cats, config] = await Promise.all([
          familia === "requisicao" ? obterRequisicao(request, id) : obterAssistencia(request, id),
          obterCatalogosSelect(request),
          obterConfiguracaoSolicitacoes(request).catch(() => null),
        ]);
        if (!vivo) return;
        setDado(detalhe);
        setCatalogos(cats);
        setCfg(config);
        setLinhas(linhasIniciais(familia === "requisicao" ? detalhe.itens : detalhe.pecas));
      } catch (e) {
        if (vivo) setErro(e?.message || "Erro ao carregar a solicitação.");
      }
    })();
    return () => { vivo = false; };
  }, [request, familia, id]);

  const itens = useMemo(
    () => (familia === "requisicao" ? dado?.itens : dado?.pecas) || [], [dado, familia],
  );
  const obrigatoria = precisaClassificar(cfg, dado?.tipo);

  // Pede a imputação sugerida à regra do backend sempre que um eixo muda.
  // Guarda a sequência por linha para a resposta antiga não vencer a nova.
  const sugerir = useCallback(async (chave, valores, aplicar) => {
    if (!EIXOS.some((e) => valores[e])) {
      aplicar("");
      return;
    }
    const seq = (sugestaoSeq.current[chave] || 0) + 1;
    sugestaoSeq.current[chave] = seq;
    try {
      const r = await obterImputacaoSugerida(request, {
        demanda: valores.demanda, processo: valores.processo_origem,
        causa: valores.causa_raiz, nc: valores.nao_conformidade,
      });
      if (sugestaoSeq.current[chave] === seq) aplicar(r?.imputacao || "");
    } catch (e) {
      /* sugestão é conveniência; sem ela a pessoa escolhe à mão */
    }
  }, [request]);

  const aplicarSugestao = (item_id) => (sug) => {
    setLinhas((prev) => prev.map((l) => {
      if (l.item_id !== item_id) return l;
      const nova = { ...l, imputacao_sugerida: sug };
      if (!l.imputacao_manual) nova.imputacao = sug;
      return nova;
    }));
  };

  const mudarLinha = (item_id) => (campo, valor, manual) => {
    setErroEnvio("");
    // A linha nova sai do estado atual (e não de dentro do updater do
    // setState, que só roda no próximo render — a sugestão nunca dispararia).
    const atual = linhas.find((l) => l.item_id === item_id);
    if (!atual) return;
    const patch = { [campo]: valor };
    if (campo === "imputacao") patch.imputacao_manual = manual !== false && Boolean(valor);
    setLinhas((prev) => prev.map((l) => (l.item_id === item_id ? { ...l, ...patch } : l)));
    if (EIXOS.includes(campo)) sugerir(item_id, { ...atual, ...patch }, aplicarSugestao(item_id));
  };

  const mudarModelo = (campo, valor, manual) => {
    const novo = { ...modelo, [campo]: valor };
    if (campo === "imputacao") novo.imputacao_manual = manual !== false && Boolean(valor);
    setModelo(novo);
    if (EIXOS.includes(campo)) {
      sugerir("__modelo__", novo, (sug) => setModelo((m) => ({
        ...m, imputacao_sugerida: sug, imputacao: m.imputacao_manual ? m.imputacao : sug,
      })));
    }
  };

  const aplicarModelo = () => {
    setErroEnvio("");
    setLinhas((prev) => aplicarATodas(prev, modelo));
  };

  const confirmar = async () => {
    setErroEnvio("");
    const incompletas = linhasIncompletas(linhas);
    if (obrigatoria && incompletas.length > 0) {
      setErroEnvio(
        `A configuração exige não conformidade e processo de origem para ${dado.tipo}: ` +
        `${incompletas.length} de ${linhas.length} item(ns) sem os dois.`,
      );
      throw new Error("Classificação incompleta.");
    }
    const classificacoes = montarClassificacoes(linhas);
    try {
      const detalhe = modo === "autorizar"
        ? await autorizarSolicitacao(request, familia, id, { classificacoes })
        : await classificarSolicitacao(request, familia, id, classificacoes);
      onDone?.(detalhe);
    } catch (e) {
      setErroEnvio(e?.message || "Não foi possível concluir.");
      throw e;
    }
  };

  const autorizando = modo === "autorizar";
  const footer = dado && catalogos && (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {erroEnvio && <InlineError style={{ fontSize: 12.5 }}>{erroEnvio}</InlineError>}
      <RowEnd>
        <GhostBtn type="button" onClick={onClose}>Voltar</GhostBtn>
        <AsyncButton variant="primary" silentError
                     successMessage={autorizando ? "Solicitação autorizada." : "Classificação salva."}
                     onClick={confirmar}>
          {autorizando ? "Autorizar e classificar" : "Salvar classificação"}
        </AsyncButton>
      </RowEnd>
    </div>
  );

  return (
    <SlideOverPanel
      open
      onClose={onClose}
      title={`${autorizando ? "Autorizar" : "Classificar"} ${dado?.numero || numero || ""}`}
      subtitle={dado ? `${TIPO_LABELS[dado.tipo] || dado.tipo} · ${itens.length} ${itens.length === 1 ? "item" : "itens"}` : ""}
      width="min(96vw, max(56vw, 640px))"
      footer={footer}
    >
      {erro && <ErrorMsg>{erro}</ErrorMsg>}
      {!erro && (!dado || !catalogos) && <Carregando />}
      {dado && catalogos && (
        <>
          <Banner>
            <FiInfo size={16} />
            <div>
              {obrigatoria
                ? <>Para <strong>{dado.tipo}</strong> a configuração exige <strong>não conformidade</strong> e <strong>processo de origem</strong> em cada item. Os outros eixos são opcionais.</>
                : <>Classificar é opcional para {dado.tipo}: o que ficar em branco entra no painel como "não informado". Quanto mais completo, mais o Painel de Assistências consegue dizer.</>}
            </div>
          </Banner>

          {itens.length > 1 && (
            <ModeloBox>
              <Label style={{ fontSize: 12.5 }}>Aplicar a todos os {itens.length} itens</Label>
              <CamposLinha linha={modelo} catalogos={catalogos} motivo="" onCampo={mudarModelo} />
              <RowEnd>
                <GhostBtn type="button" onClick={aplicarModelo}>Aplicar aos itens</GhostBtn>
              </RowEnd>
            </ModeloBox>
          )}

          <Lista>
            {itens.map((item, idx) => {
              const linha = linhas.find((l) => l.item_id === item.id);
              if (!linha) return null;
              const motivo = motivoDoItem(item);
              const incompleta = obrigatoria && (!linha.nao_conformidade || !linha.processo_origem);
              return (
                <PecaCard key={item.id} style={incompleta ? { borderColor: "var(--warning-border)" } : undefined}>
                  <PecaHead>
                    <strong>{tituloDoItem(familia, item, idx)}</strong>
                    {item.natureza === "MAO_DE_OBRA" && <Pill $tone="neutral">Mão de obra</Pill>}
                    {motivo && (
                      <Pill $tone="neutral" title="Motivo informado por quem solicitou">
                        Motivo: {item.motivo_categoria_display || item.motivo_display || motivo}
                      </Pill>
                    )}
                    {incompleta && <Pill $tone="warning">Falta NC/processo</Pill>}
                  </PecaHead>
                  {(item.descricao || formatMedidas(item)) && (
                    <PecaLinha>
                      {[item.descricao, formatMedidas(item), item.ambiente].filter(Boolean).join(" · ")}
                    </PecaLinha>
                  )}
                  <CamposLinha linha={linha} catalogos={catalogos} motivo={motivo}
                               onCampo={mudarLinha(item.id)} />
                </PecaCard>
              );
            })}
          </Lista>
        </>
      )}
    </SlideOverPanel>
  );
}
